import React, { useRef, useState, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useContractZones, useContractRoutes } from 'src/use-cases/settings/useContracts';
import { ZoneDetailsTable } from './ZoneDetailsTable';
import { RouteDetailsTable } from './RouteDetailsTable';

interface ContractDetailProps {
  contractId: string | number;
  onClose: () => void;
}

const addBtnSx = {
  width: 30,
  height: 30,
  border: '1px solid #DFDFDF',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: '#00992B',
    '& .MuiSvgIcon-root': { color: '#FFFFFF' },
  },
};

export const ContractDetail: React.FC<ContractDetailProps> = ({ contractId, onClose }) => {
  // Contract IDs are always strings (UUIDs), convert if needed
  const contractIdString = typeof contractId === 'number' ? String(contractId) : contractId;
  const { data: zonesData } = useContractZones(contractIdString);
  const zones = zonesData?.data || [];

  const { data: routesData } = useContractRoutes(contractIdString);
  const routes = routesData?.data || [];

  const zoneTableRef = useRef<any>(null);
  const routeTableRef = useRef<any>(null);

  const [splitPercent, setSplitPercent] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const { top, height } = containerRef.current.getBoundingClientRect();
      const newPercent = ((ev.clientY - top) / height) * 100;
      setSplitPercent(Math.min(Math.max(newPercent, 15), 85));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #E5E7EB',
        overflow: 'hidden',
        backgroundColor: 'white',
      }}
    >
      {/* Route pane */}
      <Box sx={{ height: `${splitPercent}%`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: 'white',
          }}
        >
          <div>
            <h4 className='text-20 font-bold text-neutral-90'>Route Details</h4>
            <p className='text-14 text-neutral-80 mt-1'>Legal Entity Aliases</p>
          </div>
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <IconButton onClick={() => routeTableRef.current?.openCreatingRow()} sx={addBtnSx}>
              <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
            </IconButton>
            <IconButton onClick={onClose} sx={{ width: 32, height: 32 }}>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <RouteDetailsTable ref={routeTableRef} contractId={contractIdString} routes={routes} />
        </Box>
      </Box>

      {/* Drag handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          height: '8px',
          flexShrink: 0,
          cursor: 'row-resize',
          backgroundColor: '#E5E7EB',
          '&:hover': { backgroundColor: '#CBD5E0' },
        }}
      />

      {/* Zone pane */}
      <Box sx={{ height: `${100 - splitPercent}%`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: 'white',
          }}
        >
          <div>
            <h4 className='text-20 font-bold text-neutral-90'>Zone Details</h4>
            <p className='text-14 text-neutral-80 mt-1'>Legal Entity Addresses</p>
          </div>
          <IconButton onClick={() => zoneTableRef.current?.openCreatingRow()} sx={addBtnSx}>
            <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <ZoneDetailsTable ref={zoneTableRef} contractId={contractIdString} zones={zones} />
        </Box>
      </Box>
    </Box>
  );
};
